import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../Image/logo.png';
import { errorMessage } from '../services/api';
import { compact, hasErrors, v } from '../utils/validation';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = compact({
      email: v.email(email, true),
      password: !password ? 'Password is required' : password.length > 72 ? 'Password is too long' : undefined,
    });
    setFieldErrors(errs);
    setError('');
    if (hasErrors(errs)) return;
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(errorMessage(err, 'Login failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ink-50">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-brand-950 via-brand-900 to-brand-700 text-white relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="absolute -left-24 bottom-0 w-80 h-80 rounded-full bg-accent-500/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex bg-white rounded-xl p-3">
            <img src={Logo} alt="ASGenix" className="h-12 object-contain" />
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl font-extrabold leading-tight">
            Every rupee in and out of <span className="text-accent-400">ASGenix</span>, in one place.
          </h1>
          <p className="mt-4 text-white/70 max-w-md">
            Client subscriptions and GST invoices, salaries, allowances, AI tools, internet and every other
            company expense — with live profit and cash position.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[
              ['fa-file-invoice-dollar', 'Billing'],
              ['fa-receipt', 'Expenses'],
              ['fa-chart-line', 'Reports'],
            ].map(([icon, label]) => (
              <div key={label} className="rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-center">
                <i className={`fas ${icon} text-accent-400`}></i>
                <p className="text-xs mt-1.5 font-semibold text-white/80">{label}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-white/40 tracking-[0.2em]">INNOVATE • INTEGRATE • INSPIRE</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center">
            <img src={Logo} alt="ASGenix" className="h-12 object-contain" />
          </div>
          <h2 className="text-2xl font-extrabold text-ink-900">Sign in</h2>
          <p className="text-sm text-ink-500 mt-1 mb-6">ASGenix Account — finance console</p>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              label="Email"
              type="email"
              icon="fa-envelope"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setFieldErrors((p) => ({ ...p, email: undefined })); }}
              placeholder="you@asgenix.com"
              error={fieldErrors.email}
              maxLength={255}
              autoComplete="username"
            />
            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                icon="fa-lock"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setFieldErrors((p) => ({ ...p, password: undefined })); }}
                placeholder="••••••••"
                error={fieldErrors.password}
                maxLength={72}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-[34px] text-ink-400 hover:text-ink-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
              </button>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                <i className="fas fa-circle-exclamation mr-1.5"></i>
                {error}
              </div>
            )}

            <Button type="submit" fullWidth disabled={submitting} icon={submitting ? 'fa-spinner fa-spin' : 'fa-right-to-bracket'}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
